import { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Package,
  ArrowRight,
  Image as ImageIcon,
  Filter,
  Info,
} from "lucide-react";
import { brandService } from "../../lib/brandService";
import { toast } from "sonner";
import { AffiliateHeaderContext } from "../../context/AffiliateHeaderContext";
import FilterBrandModal from "./FilterBrandModal";

const ProductsPage = () => {
  const [brands, setBrands] = useState([]);
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [selectedBrandIds, setSelectedBrandIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const navigate = useNavigate();
  const { affiliateName } = useParams();
  const setHeaderContent = useContext(AffiliateHeaderContext);

  // Set header content
  useEffect(() => {
    setHeaderContent(
      <div className="flex items-center space-x-4">
        <div className="w-2 h-8 bg-gradient-to-b from-[#B8460E] to-[#8B3A0E] rounded-full"></div>
        <div>
          <h1 className="text-2xl font-bold text-white">Products</h1>
          <p className="text-gray-400 text-sm">
            Explore our product catalog with detailed marketing insights
          </p>
        </div>
      </div>
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  useEffect(() => {
    fetchBrandsWithProducts();
  }, []);

  useEffect(() => {
    // Filter brands based on selected brand IDs
    if (selectedBrandIds.length === 0) {
      setFilteredBrands([]); // Show no brands when none are selected
    } else {
      setFilteredBrands(
        brands.filter((brand) => selectedBrandIds.includes(brand.id))
      );
    }
  }, [brands, selectedBrandIds]);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFilterPopup && !event.target.closest(".filter-popup")) {
        setShowFilterPopup(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFilterPopup]);

  const fetchBrandsWithProducts = async () => {
    setLoading(true);
    const result = await brandService.getAllBrands();
    if (result.success) {
      // Only include active brands with active products
      const activeBrands = result.data
        .filter((brand) => brand.is_active)
        .map((brand) => ({
          ...brand,
          products: brand.products.filter((product) => product.is_active),
        }))
        .filter((brand) => brand.products.length > 0); // Only brands with products

      console.log("Fetched brands with products:", activeBrands);
      setBrands(activeBrands);
      setFilteredBrands(activeBrands);

      // Select all brands by default
      setSelectedBrandIds(activeBrands.map((brand) => brand.id));
    } else {
      toast.error("Failed to fetch products");
    }
    setLoading(false);
  };

  const createProductSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .replace(/-+/g, "-") // Replace multiple hyphens with single
      .trim();
  };

  const handleProductClick = (product) => {
    const slug = createProductSlug(product.name);
    navigate(`/affiliate/${affiliateName}/products/${slug}`, {
      state: { productId: product.id },
    });
  };

  const getTotalProductCount = () => {
    return filteredBrands.reduce(
      (total, brand) => total + brand.products.length,
      0
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#B8460E] mx-auto mb-4"></div>
          <p className="text-white">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 relative pb-20">
      {/* Floating Filter Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setShowFilterPopup(!showFilterPopup)}
          className="relative bg-[#B8460E] hover:bg-[#8B3A0E] text-white w-14 h-14 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 flex items-center justify-center group transform hover:scale-110"
        >
          <div className="relative">
            <Filter className="w-6 h-6 transition-transform duration-300 group-hover:rotate-45" />
            {/* Badge for selected brands count */}
            {selectedBrandIds.length > 0 && (
              <div className="absolute -top-6 -right-4 bg-white text-[#B8460E] text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center shadow-lg">
                {selectedBrandIds.length}
              </div>
            )}
          </div>
        </button>
      </div>

      {/* Filter Brand Modal */}
      <FilterBrandModal
        isOpen={showFilterPopup}
        onClose={() => setShowFilterPopup(false)}
        brands={brands}
        selectedBrandIds={selectedBrandIds}
        onApplyFilters={(newSelectedBrandIds) =>
          setSelectedBrandIds(newSelectedBrandIds)
        }
        getTotalProductCount={getTotalProductCount}
        filteredBrands={filteredBrands}
      />

      {/* Products by Brand */}
      {filteredBrands.length > 0 ? (
        <div className="space-y-8">
          {filteredBrands.map((brand) => (
            <div key={brand.id} className="space-y-4">
              {/* Brand Header */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-[#B8460E] rounded-full"></div>
                  <h2 className="text-2xl font-bold text-white">
                    {brand.name}
                  </h2>
                  {brand.description && (
                    <div className="relative group">
                      <Info className="w-5 h-5 text-gray-400 hover:text-[#B8460E] transition-colors cursor-help" />
                      {/* Tooltip */}
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 lg:left-0 lg:transform-none mt-2 px-4 py-3 bg-gray-900/95 backdrop-blur-xl border border-white/10 rounded-lg text-sm text-white w-[90vw] max-w-[90vw] lg:w-96 lg:max-w-96 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[9999]">
                        <div className="text-left">{brand.description}</div>
                        {/* Tooltip Arrow */}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 lg:left-4 lg:transform-none w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-gray-900/95"></div>
                      </div>
                    </div>
                  )}
                  <span className="bg-white/10 text-gray-300 px-3 py-1 rounded-full text-sm">
                    {brand.products.length} product
                    {brand.products.length !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>

              {/* Products Grid for this Brand */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {brand.products.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    brandName={brand.name}
                    onClick={() => handleProductClick(product)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-12 text-center">
          <div className="w-16 h-16 bg-[#B8460E]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="w-8 h-8 text-[#B8460E]" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">
            {selectedBrandIds.length > 0
              ? "No Products Found"
              : "No Products Available"}
          </h3>
          <p className="text-gray-400">
            {selectedBrandIds.length > 0
              ? "Try selecting different brands or clear the filters."
              : "Check back later for new products and marketing materials."}
          </p>
          {selectedBrandIds.length > 0 && (
            <button
              onClick={() => setSelectedBrandIds([])}
              className="mt-4 bg-[#B8460E]/20 hover:bg-[#B8460E]/30 text-[#B8460E] px-4 py-2 rounded-lg transition-all duration-200"
            >
              Clear Filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const ProductCard = ({ product, brandName, onClick }) => {
  return (
    <div
      onClick={onClick}
      className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 transition-all duration-300 cursor-pointer group shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-32 h-32 bg-[#B8460E]/5 rounded-full blur-2xl animate-pulse -top-8 -right-8"></div>
        <div className="absolute w-24 h-24 bg-[#8B3A0E]/5 rounded-full blur-xl animate-pulse delay-1000 -bottom-6 -left-6"></div>
      </div>

      {/* Product Image - 1:1 Aspect Ratio */}
      <div className="relative aspect-square bg-gradient-to-br from-gray-800 to-gray-900 overflow-hidden">
        {product.main_image_url ? (
          <img
            src={product.main_image_url}
            alt={product.name}
            className="w-full h-full object-cover transition-all duration-500 group-hover:scale-110 group-hover:rotate-1"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-500 bg-gradient-to-br from-gray-800 to-gray-900">
            <div className="text-center">
              <ImageIcon className="w-16 h-16 mx-auto mb-2 opacity-50" />
              <p className="text-xs text-gray-600">No Image</p>
            </div>
          </div>
        )}

        {/* Modern Hover Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300">
          <div className="absolute bottom-4 left-4 right-4">
            <div className="bg-white/20 backdrop-blur-md border border-white/30 rounded-xl px-4 py-2 transform translate-y-8 group-hover:translate-y-0 transition-transform duration-300">
              <div className="flex items-center justify-between">
                <span className="text-white text-sm font-medium">
                  View Product
                </span>
                <ArrowRight className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Shine Effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 pointer-events-none"></div>
      </div>

      {/* Product Info */}
      <div className="relative z-10 p-6 space-y-4">
        <div>
          <h3 className="text-lg font-bold text-white mb-2 group-hover:text-[#B8460E] transition-colors duration-200 line-clamp-1">
            {product.name}
          </h3>
          <p className="text-gray-400 text-sm line-clamp-2 leading-relaxed">
            {product.short_description ||
              "Explore this product's detailed information and marketing materials"}
          </p>
        </div>
        
      </div>
    </div>
  );
};

export default ProductsPage;
